import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faEdit, faPlus, faToggleOn, faToggleOff, faShieldAlt } from '@fortawesome/free-solid-svg-icons';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Swal from 'sweetalert2';
import './PaymentMethodsData.css';
import baseURL from '../../url';

export default function PaymentMethodsData() {
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [modalVisible, setModalVisible] = useState(false);
    const [modalMode, setModalMode] = useState('create'); // 'create' o 'edit'
    const [currentMethod, setCurrentMethod] = useState({});

    // Campos del formulario
    const [name, setName] = useState('');
    const [instructions, setInstructions] = useState('');
    const [imagePath, setImagePath] = useState('');
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState('');
    const [uploadingImage, setUploadingImage] = useState(false);
    const [requiresProof, setRequiresProof] = useState(false);
    const [active, setActive] = useState(true);

    useEffect(() => {
        cargarMetodosPago();
    }, []);

    const cargarMetodosPago = () => {
        fetch(`${baseURL}/paymentMethodsGet.php`)
            .then(response => response.json())
            .then(data => {
                if (data.success) {
                    setPaymentMethods(data.payment_methods || []);
                }
            })
            .catch(error => console.error('Error al cargar métodos de pago:', error));
    };

    const abrirModalNuevo = () => {
        setModalMode('create');
        setCurrentMethod({});
        limpiarFormulario();
        setModalVisible(true);
    };

    const abrirModalEditar = (method) => {
        setModalMode('edit');
        setCurrentMethod(method);
        setName(method.name || '');
        setInstructions(method.instructions || '');
        setImagePath(method.image_path || '');
        setImagePreview(method.image_path ? `${baseURL}/${method.image_path}` : '');
        setRequiresProof(method.requires_proof == 1);
        setActive(method.active == 1);
        setModalVisible(true);
    };

    const cerrarModal = () => {
        setModalVisible(false);
        limpiarFormulario();
    };

    const limpiarFormulario = () => {
        setName('');
        setInstructions('');
        setImagePath('');
        setImageFile(null);
        setImagePreview('');
        setRequiresProof(false);
        setActive(true);
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Validar tipo de archivo
            const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
            if (!allowedTypes.includes(file.type)) {
                toast.error('Solo se permiten archivos de imagen (JPG, PNG, GIF, WEBP)');
                return;
            }

            // Validar tamaño (5MB máximo)
            if (file.size > 5 * 1024 * 1024) {
                toast.error('La imagen es demasiado grande. Máximo 5MB');
                return;
            }

            setImageFile(file);
            setImagePreview(URL.createObjectURL(file));
        }
    };

    const subirImagen = async () => {
        if (!imageFile) return null;

        setUploadingImage(true);
        const formData = new FormData();
        formData.append('image', imageFile);

        try {
            const response = await fetch(`${baseURL}/paymentMethodUploadImage.php`, {
                method: 'POST',
                body: formData
            });
            const data = await response.json();
            setUploadingImage(false);

            if (data.success) {
                return data.imagePath;
            } else {
                toast.error(data.error || 'Error al subir la imagen');
                return null;
            }
        } catch (error) {
            setUploadingImage(false);
            toast.error('Error al subir la imagen');
            console.error('Error:', error);
            return null;
        }
    };

    const eliminarImagen = () => {
        setImageFile(null);
        setImagePreview('');
        setImagePath('');
    };

    const guardarMetodoPago = async () => {
        if (!name.trim()) {
            toast.error('El nombre del método de pago es obligatorio');
            return;
        }

        // Si hay una nueva imagen, subirla primero
        let uploadedImagePath = imagePath; // Mantener la existente si no hay nueva
        if (imageFile) {
            const newImagePath = await subirImagen();
            if (newImagePath) {
                uploadedImagePath = newImagePath;
            } else {
                // Si falló la subida y no hay imagen previa, no continuar
                if (!imagePath) {
                    toast.error('Error al subir la imagen');
                    return;
                }
            }
        }

        const formData = new FormData();
        
        if (modalMode === 'edit') {
            formData.append('idPaymentMethod', currentMethod.idPaymentMethod);
            
            // Solo enviar campos que pueden editarse
            if (currentMethod.is_system != 1) {
                formData.append('name', name);
                formData.append('instructions', instructions);
                formData.append('image_path', uploadedImagePath || '');
            }
            formData.append('requires_proof', requiresProof ? 1 : 0);
            formData.append('active', active ? 1 : 0);

            fetch(`${baseURL}/paymentMethodsUpdate.php`, {
                method: 'POST',
                body: formData
            })
                .then(response => response.json())
                .then(data => {
                    if (data.success) {
                        toast.success(data.message);
                        cargarMetodosPago();
                        cerrarModal();
                    } else {
                        toast.error(data.message);
                    }
                })
                .catch(error => {
                    console.error('Error al actualizar método:', error);
                    toast.error('Error al procesar la solicitud');
                });
        } else {
            // Crear nuevo
            formData.append('name', name);
            formData.append('instructions', instructions);
            formData.append('image_path', uploadedImagePath || '');
            formData.append('requires_proof', requiresProof ? 1 : 0);
            formData.append('active', active ? 1 : 0);

            fetch(`${baseURL}/paymentMethodsSave.php`, {
                method: 'POST',
                body: formData
            })
                .then(response => response.json())
                .then(data => {
                    if (data.success) {
                        toast.success(data.message);
                        cargarMetodosPago();
                        cerrarModal();
                    } else {
                        toast.error(data.message);
                    }
                })
                .catch(error => {
                    console.error('Error al crear método:', error);
                    toast.error('Error al procesar la solicitud');
                });
        }
    };

    const toggleActivoMetodo = (method) => {
        const accion = method.active == 1 ? 'desactivar' : 'activar';
        
        Swal.fire({
            title: `¿${accion.charAt(0).toUpperCase() + accion.slice(1)} método de pago?`,
            text: `¿Está seguro de ${accion} "${method.name}"?`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: `Sí, ${accion}`,
            cancelButtonText: 'Cancelar'
        }).then((result) => {
            if (result.isConfirmed) {
                const formData = new FormData();
                formData.append('idPaymentMethod', method.idPaymentMethod);

                fetch(`${baseURL}/paymentMethodsToggle.php`, {
                    method: 'POST',
                    body: formData
                })
                    .then(response => response.json())
                    .then(data => {
                        if (data.success) {
                            toast.success(data.message);
                            cargarMetodosPago();
                        } else {
                            toast.error(data.message);
                        }
                    })
                    .catch(error => {
                        console.error('Error al cambiar estado:', error);
                        toast.error('Error al procesar la solicitud');
                    });
            }
        });
    };

    const eliminarMetodo = (method) => {
        Swal.fire({
            title: '¿Eliminar método de pago?',
            text: `Esta acción no se puede deshacer. ¿Está seguro de eliminar "${method.name}"?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar'
        }).then((result) => {
            if (result.isConfirmed) {
                const formData = new FormData();
                formData.append('idPaymentMethod', method.idPaymentMethod);

                fetch(`${baseURL}/paymentMethodsDelete.php`, {
                    method: 'POST',
                    body: formData
                })
                    .then(response => response.json())
                    .then(data => {
                        if (data.success) {
                            toast.success(data.message);
                            cargarMetodosPago();
                        } else {
                            toast.error(data.message);
                        }
                    })
                    .catch(error => {
                        console.error('Error al eliminar método:', error);
                        toast.error('Error al procesar la solicitud');
                    });
            }
        });
    };

    return (
        <div className='paymentMethodsContainer'>
            <ToastContainer />

            <div className='paymentMethodsHeader'>
                <h2>Medios de Pago</h2>
                <button className='btnAgregar' onClick={abrirModalNuevo}>
                    <FontAwesomeIcon icon={faPlus} /> Agregar Método
                </button>
            </div>

            <div className='paymentMethodsTable'>
                <table>
                    <thead>
                        <tr>
                            <th>ID</th>
                            <th>Nombre</th>
                            <th>Requiere Comprobante</th>
                            <th>Estado</th>
                            <th>Tipo</th>
                            <th>Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {paymentMethods.length > 0 ? (
                            paymentMethods.map((method) => (
                                <tr key={method.idPaymentMethod}>
                                    <td>{method.idPaymentMethod}</td>
                                    <td className='methodName'>{method.name}</td>
                                    <td>
                                        <span className={method.requires_proof == 1 ? 'badge-si' : 'badge-no'}>
                                            {method.requires_proof == 1 ? 'Sí' : 'No'}
                                        </span>
                                    </td>
                                    <td>
                                        <span className={method.active == 1 ? 'badge-activo' : 'badge-inactivo'}>
                                            {method.active == 1 ? 'Activo' : 'Inactivo'}
                                        </span>
                                    </td>
                                    <td>
                                        {method.is_system == 1 && (
                                            <span className='badge-sistema'>
                                                <FontAwesomeIcon icon={faShieldAlt} /> Sistema
                                            </span>
                                        )}
                                    </td>
                                    <td className='accionesCol'>
                                        <button
                                            className='btnAccion btnEditar'
                                            onClick={() => abrirModalEditar(method)}
                                            title='Editar'
                                        >
                                            <FontAwesomeIcon icon={faEdit} />
                                        </button>
                                        <button
                                            className={`btnAccion ${method.active == 1 ? 'btnDesactivar' : 'btnActivar'}`}
                                            onClick={() => toggleActivoMetodo(method)}
                                            title={method.active == 1 ? 'Desactivar' : 'Activar'}
                                        >
                                            <FontAwesomeIcon icon={method.active == 1 ? faToggleOff : faToggleOn} />
                                        </button>
                                        {method.is_system != 1 && (
                                            <button
                                                className='btnAccion btnEliminar'
                                                onClick={() => eliminarMetodo(method)}
                                                title='Eliminar'
                                            >
                                                <FontAwesomeIcon icon={faTrash} />
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan='6' className='sinDatos'>
                                    No hay métodos de pago registrados
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Modal */}
            {modalVisible && (
                <div className='modalOverlay' onClick={cerrarModal}>
                    <div className='modalContent' onClick={(e) => e.stopPropagation()}>
                        <div className='modalHeader'>
                            <h3>{modalMode === 'create' ? 'Nuevo Método de Pago' : 'Editar Método de Pago'}</h3>
                            <button className='btnCerrarModal' onClick={cerrarModal}>✕</button>
                        </div>
                        <div className='modalBody'>
                            <div className='formGroup'>
                                <label>Nombre del método *</label>
                                <input
                                    type='text'
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    placeholder='Ej: Transferencia bancaria, Nequi, etc.'
                                    disabled={currentMethod.is_system == 1}
                                />
                                {currentMethod.is_system == 1 && (
                                    <small className='helpText'>No se puede editar el nombre de métodos del sistema</small>
                                )}
                            </div>

                            <div className='formGroup'>
                                <label>Instrucciones (opcional)</label>
                                <textarea
                                    value={instructions}
                                    onChange={(e) => setInstructions(e.target.value)}
                                    placeholder='Instrucciones para el cliente. Ej: Escanea el QR y envía el comprobante...'
                                    rows='4'
                                    disabled={currentMethod.is_system == 1}
                                />
                                {currentMethod.is_system == 1 && (
                                    <small className='helpText'>No se pueden editar las instrucciones de métodos del sistema</small>
                                )}
                            </div>

                            {currentMethod.is_system != 1 && (
                                <div className='formGroup'>
                                    <label>Imagen (opcional) - Ej: QR de banco</label>
                                    <input
                                        type='file'
                                        accept='image/*'
                                        onChange={handleImageChange}
                                        className='fileInput'
                                    />
                                    <small className='helpText'>
                                        Sube una imagen (QR, logo, etc.). Máximo 5MB. Se mostrará automáticamente al cliente.
                                    </small>
                                    
                                    {(imagePreview || uploadingImage) && (
                                        <div className='imagePreviewContainer'>
                                            {uploadingImage && <p className='uploadingText'>Subiendo imagen...</p>}
                                            {imagePreview && !uploadingImage && (
                                                <>
                                                    <img src={imagePreview} alt='Preview' className='imagePreview' />
                                                    <button 
                                                        type='button' 
                                                        className='btnEliminarImagen' 
                                                        onClick={eliminarImagen}
                                                    >
                                                        ✕ Eliminar imagen
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}

                            <div className='formGroup checkboxGroup'>
                                <label className='checkboxLabel'>
                                    <input
                                        type='checkbox'
                                        checked={requiresProof}
                                        onChange={(e) => setRequiresProof(e.target.checked)}
                                    />
                                    <span>Requiere comprobante de pago obligatorio</span>
                                </label>
                            </div>

                            <div className='formGroup checkboxGroup'>
                                <label className='checkboxLabel'>
                                    <input
                                        type='checkbox'
                                        checked={active}
                                        onChange={(e) => setActive(e.target.checked)}
                                    />
                                    <span>Activo (visible en el frontend)</span>
                                </label>
                            </div>
                        </div>
                        <div className='modalFooter'>
                            <button className='btnCancelar' onClick={cerrarModal}>Cancelar</button>
                            <button className='btnGuardar' onClick={guardarMetodoPago}>
                                {modalMode === 'create' ? 'Crear' : 'Guardar cambios'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}




